import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../db/firebase";

export type TimeOffStatus = "pending" | "approved" | "rejected" | "cancelled";

export interface TimeOffRequest {
  id: string;
  organizationId: string;
  userId: string;
  userName: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  reason?: string;
  status: TimeOffStatus;
  createdAt?: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
}

/** Inclusive range check for YYYY-MM-DD strings. */
export function isDateInTimeOffRange(
  date: string,
  startDate: string,
  endDate: string,
): boolean {
  if (!date || !startDate || !endDate) return false;
  return date >= startDate && date <= endDate;
}

export function dateOverlapsRequest(
  date: string,
  request: Pick<TimeOffRequest, "startDate" | "endDate" | "status">,
  statuses: TimeOffStatus[] = ["approved"],
): boolean {
  if (!statuses.includes(request.status)) return false;
  return isDateInTimeOffRange(date, request.startDate, request.endDate);
}

type TimeOffMap = Record<
  string,
  {
    startDate?: string;
    endDate?: string;
    reason?: string;
    status?: TimeOffStatus;
    createdAt?: string | null;
    reviewedAt?: string | null;
    reviewedBy?: string | null;
  }
>;

function requestsFromUserDoc(
  userId: string,
  organizationId: string,
  userName: string,
  data: Record<string, unknown>,
): TimeOffRequest[] {
  const map = (data.timeOffRequests || {}) as TimeOffMap;
  return Object.entries(map)
    .map(([id, value]) => ({
      id,
      organizationId,
      userId,
      userName,
      startDate: value.startDate || "",
      endDate: value.endDate || "",
      reason: value.reason || "",
      status: (value.status || "pending") as TimeOffStatus,
      createdAt: value.createdAt ?? null,
      reviewedAt: value.reviewedAt ?? null,
      reviewedBy: value.reviewedBy ?? null,
    }))
    .filter((r) => r.startDate && r.endDate);
}

/** Load all org time-off from user documents (works with existing Firestore rules). */
export async function fetchOrgTimeOffRequests(
  organizationId: string,
): Promise<TimeOffRequest[]> {
  const snap = await getDocs(
    query(
      collection(db, "users"),
      where("organizationId", "==", organizationId),
    ),
  );

  const rows: TimeOffRequest[] = [];
  snap.docs.forEach((d) => {
    const data = d.data();
    const name =
      [data.firstName, data.lastName].filter(Boolean).join(" ").trim() ||
      data.email ||
      "User";
    rows.push(...requestsFromUserDoc(d.id, organizationId, name, data));
  });
  return rows;
}

export async function createTimeOffRequest(params: {
  organizationId: string;
  userId: string;
  userName: string;
  startDate: string;
  endDate: string;
  reason: string;
  requestId: string;
}): Promise<void> {
  const { organizationId, userId, userName, startDate, endDate, reason, requestId } =
    params;
  await updateDoc(doc(db, "users", userId), {
    [`timeOffRequests.${requestId}`]: {
      organizationId,
      userName,
      startDate,
      endDate,
      reason,
      status: "pending",
      createdAt: new Date().toISOString(),
      reviewedAt: null,
      reviewedBy: null,
    },
  });
}

export async function updateTimeOffRequestStatus(params: {
  userId: string;
  requestId: string;
  status: TimeOffStatus;
  reviewedBy?: string | null;
}): Promise<void> {
  const { userId, requestId, status, reviewedBy = null } = params;
  await updateDoc(doc(db, "users", userId), {
    [`timeOffRequests.${requestId}.status`]: status,
    [`timeOffRequests.${requestId}.reviewedAt`]: new Date().toISOString(),
    [`timeOffRequests.${requestId}.reviewedBy`]: reviewedBy,
  });
}

export async function userHasApprovedTimeOffOnDate(
  organizationId: string,
  userId: string,
  date: string,
): Promise<boolean> {
  if (!organizationId || !userId || !date) return false;
  const requests = await fetchOrgTimeOffRequests(organizationId);
  return requests.some(
    (r) =>
      r.userId === userId && dateOverlapsRequest(date, r, ["approved"]),
  );
}

/** Set of userIds unavailable on the given event date. */
export async function getUnavailableUserIdsOnDate(
  organizationId: string,
  date: string,
  userIds: string[],
): Promise<Set<string>> {
  const unavailable = new Set<string>();
  if (!organizationId || !date || userIds.length === 0) return unavailable;

  const idSet = new Set(userIds);
  const requests = await fetchOrgTimeOffRequests(organizationId);
  requests.forEach((data) => {
    if (data.status !== "approved") return;
    if (!idSet.has(data.userId)) return;
    if (isDateInTimeOffRange(date, data.startDate, data.endDate)) {
      unavailable.add(data.userId);
    }
  });

  return unavailable;
}
